import { merge } from 'lodash';

import { TranslationFile } from '@bolteu/translations-service';
import { TranslationsApiClient } from '@bolteu/translations-service/src/compatibility-types';

import config from '../config';
import { ProviderPortalModuleSetup } from '../Module';
import { LanguageUtils } from '../utils/translations';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const crowdInLanguages = require('@bolteu/translations-service/src/crowdin-supported-languages.json');

const HTTP_SUCCESS_CODE = 200;

export interface TranslationServiceConfig {
  remoteModuleFolder: string;
  moduleDefaultTranslations: TranslationFile;
}

// TODO: figure out why Lattice is not accepting the file with contexts. Might be using older translations interface?
// TODO: remove this
const transformTranslationsWithContext = (translations: TranslationFile): TranslationFile => {
  return Object.entries(translations).reduce<TranslationFile>((result, [key, value]) => {
    if (typeof value === 'object' && 'text' in value && 'context' in value) {
      return {
        ...result,
        [key]: value.text,
      };
    }

    return {
      ...result,
      [key]: transformTranslationsWithContext(value as TranslationFile),
    };
  }, {});
};

export class TranslationService {
  private moduleInstance: ProviderPortalModuleSetup;
  private translationsApiClient: TranslationsApiClient;
  private translations: TranslationFile = {};
  private defaultTranslations: TranslationFile = {};

  constructor(module: ProviderPortalModuleSetup, apiClient: TranslationsApiClient) {
    this.moduleInstance = module;
    this.translationsApiClient = apiClient;
  }

  public loadRemoteModuleTranslations(translationModuleConfig: TranslationServiceConfig) {
    this.defaultTranslations = transformTranslationsWithContext(
      translationModuleConfig.moduleDefaultTranslations,
    );
    this.syncTranslations(translationModuleConfig)
      .then((response) => {
        this.translations = merge({}, this.defaultTranslations, response as TranslationFile);
        this.moduleInstance.translationLoaded();
      })
      .catch(() => {
        this.translations = this.defaultTranslations;
        this.moduleInstance.translationLoaded();
      });
  }

  public getCurrentLocale() {
    return LanguageUtils.getAppLocaleWithFallback();
  }

  public getModuleTranslations() {
    return this.translations;
  }

  private async syncTranslations(translationModuleConfig: TranslationServiceConfig) {
    const currentLocale = this.getCurrentLocale();
    const query = {
      language: TranslationService.getCrowdInCode(currentLocale),
      project: config.crowdInProject,
      file: `${config.crowdInBaseTranslationsFolderName}/${translationModuleConfig.remoteModuleFolder}/translations.json`,
    };

    try {
      const response = await this.translationsApiClient.exportFileHttp(query);

      if (!response || response.status !== HTTP_SUCCESS_CODE) {
        return this.defaultTranslations;
      }
      return response.body;
    } catch (_e) {
      return this.defaultTranslations;
    }
  }

  private static mapLocaleToCrowdInCode(languages: { locale: string; crowdin_code: string }[]) {
    return languages.reduce((acc, item) => {
      acc[item.locale] = item.crowdin_code;
      return acc;
    }, {} as { [key: string]: string });
  }

  private static getCrowdInCode(locale: string) {
    return TranslationService.mapLocaleToCrowdInCode(crowdInLanguages)[locale] || locale;
  }
}
